import { Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CoursDossier, CritereData, CritereNotation, DossierPedagogique, Eleve, EvaluationConfig, EvaluationDossierItem, HistoriqueAnneeScolaire, InscriptionDossier } from '@app/models/enseignant';
import { EnseignantService } from '@app/services/enseignant.service';
import { formatText } from '@app/utils/text';

export interface DossierPedagogiqueDialogData {
  eleve: Eleve;
}

export interface EvaluationAvecCours {
  evalItem: EvaluationDossierItem;
  cours: CoursDossier[];
  coursAssocie: CoursDossier | null;
  uniqueDisciplines: Pick<CoursDossier, 'discipline' | 'couleurDiscipline'>[];
}

@Component({
  selector: 'app-dossier-pedagogique-dialog',
  standalone: false,
  templateUrl: './dossier-pedagogique-dialog.component.html',
  styleUrl: './dossier-pedagogique-dialog.component.scss'
})
export class DossierPedagogiqueDialogComponent implements OnInit {
  data: DossierPedagogiqueDialogData = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<DossierPedagogiqueDialogComponent>);
  enseignantService = inject(EnseignantService);

  isLoading = signal(true);
  historique = signal<HistoriqueAnneeScolaire[]>([]);
  config: EvaluationConfig;

  get eleve() { return this.data.eleve; }

  ngOnInit(): void {
    this.enseignantService.getEvaluationConfig().subscribe(conf => this.config = conf);
    this.enseignantService
      .getHistoriqueEleve(this.eleve.idEnfant ?? 0, this.eleve.idAdulte ?? 0)
      .subscribe((res: DossierPedagogique) => {
        const sorted = [...(res?.historique ?? [])].reverse();
        this.historique.set(sorted);
        this.isLoading.set(false);
      });
  }

  getEvaluationsAvecCours(inscription: InscriptionDossier): EvaluationAvecCours[] {
    const map = new Map<number, { evalItem: EvaluationDossierItem; cours: CoursDossier[] }>();
    for (const cours of inscription.cours) {
      for (const evalItem of cours.evaluations) {
        const id = evalItem.evaluation.idEvaluation;
        if (!map.has(id)) map.set(id, { evalItem, cours: [] });
        map.get(id)!.cours.push(cours);
      }
    }
    return [...map.values()]
      .map(({ evalItem, cours }) => ({
        evalItem,
        cours,
        coursAssocie: cours.find(c => c.id === evalItem.evaluation.idCours) ?? null,
        uniqueDisciplines: this.uniqueDisciplines(cours),
      }))
      .sort((a, b) =>
        a.evalItem.evaluation.dateDebut.localeCompare(b.evalItem.evaluation.dateDebut)
      );
  }

  private uniqueDisciplines(cours: CoursDossier[]): Pick<CoursDossier, 'discipline' | 'couleurDiscipline'>[] {
    const seen = new Set<string>();
    const result: Pick<CoursDossier, 'discipline' | 'couleurDiscipline'>[] = [];
    for (const c of cours) {
      if (seen.has(c.discipline)) continue;
      seen.add(c.discipline);
      result.push({ discipline: c.discipline, couleurDiscipline: c.couleurDiscipline });
    }
    return result;
  }

  formatText(str: string): string {
    return formatText(str);
  }

  buildCriteresData(evalItem: EvaluationDossierItem): CritereData {
    if (!evalItem.criteresNotation?.length || !evalItem._TabBOEvaluationInscriptionCritereNotation?.length) {
      return {};
    }
    const criteresMap = new Map<number, string>(evalItem.criteresNotation.map(c => [c.id, c.type]));
    const data: CritereData = {};
    for (const rep of evalItem._TabBOEvaluationInscriptionCritereNotation) {
      const type = criteresMap.get(rep.idCritereNotation);
      data[rep.idCritereNotation] =
        rep.idCritereNotationValeur && rep.idCritereNotationValeur !== 0
          ? rep.idCritereNotationValeur
          : this.enseignantService.parseValeurCritereNotation(rep.valeur, type);
    }
    return data;
  }

  private tryValidate(critere: CritereNotation, criteresData: CritereData): boolean {
    try {
      return this.enseignantService.validateCritere(critere, criteresData[critere.id]);
    } catch {
      return false;
    }
  }

  getCritereStatus(critere: CritereNotation, criteresData: CritereData): boolean | null {
    if (critere.type !== 'liste' && !critere.valeurReussite) return null;
    if (!criteresData) return null;
    return this.tryValidate(critere, criteresData);
  }

  getSyntheseStatsForEval(criteresNotation: CritereNotation[], criteresData: CritereData) {
    if (!criteresNotation?.length || !criteresData) return null;
    const compute = (list: CritereNotation[]) => {
      const total = list.length;
      const reussi = list.filter(c => this.tryValidate(c, criteresData)).length;
      return { total, reussi, echec: total - reussi };
    };
    return {
      obligatoires: compute(criteresNotation.filter(c => c.obligatoire)),
      nonObligatoires: compute(criteresNotation.filter(c => !c.obligatoire)),
      tous: compute(criteresNotation)
    };
  }

  groupCriteresByCode(criteresNotation: CritereNotation[]): { code: string; criteres: CritereNotation[] }[] {
    if (!criteresNotation) return [];
    const groups = new Map<string, CritereNotation[]>();
    for (const critere of criteresNotation) {
      const code = critere.code ?? '';
      if (!groups.has(code)) groups.set(code, []);
      groups.get(code).push(critere);
    }
    return Array.from(groups.entries()).map(([code, criteres]) => ({ code, criteres }));
  }

}
